import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import { ListMeta, UserName } from './adminUsers';

export type KycProvider = 'VERIFF' | 'MANUAL';

export type KycStatus = 'PENDING' | 'UNVERIFIED' | 'VERIFIED' | 'CANCELED' | 'EXPIRED' | 'FAILED';

/** Veriff's own session state, which moves independently of our `status`. */
export type VeriffSessionStatus =
  | 'created'
  | 'started'
  | 'submitted'
  | 'approved'
  | 'declined'
  | 'resubmission_requested'
  | 'review'
  | 'expired'
  | 'abandoned';

export type VeriffPerson = {
  first_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  nationality?: string | null;
  gender?: string | null;
  id_number?: string | null;
};

export type VeriffDocument = {
  type?: string | null;
  number?: string | null;
  country?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  nfc_validated?: boolean | null;
};

export type VeriffBlock = {
  session_id?: string;
  /** The customer's own frame URL — never opened from the admin panel. */
  session_url?: string;
  session_created_at?: string;
  attempt_id?: string;
  status?: VeriffSessionStatus | string;
  decision_code?: number;
  reason?: string | null;
  reason_code?: number | null;
  risk_score?: number | null;
  risk_labels?: string[];
  person?: VeriffPerson | null;
  document?: VeriffDocument | null;
  decision_time?: string;
};

export type KycUserSummary = {
  id: string;
  _id?: string;
  name?: UserName | null;
  email?: string | null;
  bxlend_id?: string | null;
};

export type KycRecord = {
  id: string;
  /** Mongo's own key, which some list payloads still use instead of `id`. */
  _id?: string;
  /** An ObjectId on show/update, a summary object on list. */
  user?: string | KycUserSummary | null;
  provider?: KycProvider | string;
  status?: KycStatus | string;
  requires_manual_review?: boolean;
  resubmission_allowed?: boolean;
  response_message?: string | null;
  name?: UserName | null;
  country_code?: string | null;
  identification_type?: string | null;
  veriff?: VeriffBlock | null;
  created_at?: string;
  updated_at?: string;

  // Legacy MANUAL records only.
  address?: { city?: string; pin_code?: string; full_address?: string } | null;
  identification_url?: { front?: string; back?: string } | null;
  photo_url?: string | null;
  terms_and_conditions_consent?: boolean;
  privacy_policy_consent?: boolean;
};

export type KycListItem = KycRecord & {
  user_id?: string;
  user?: KycUserSummary | null;
};

export type KycSearchField = 'name' | 'email' | 'country_code' | 'id';

export type ReverifySession = {
  session_id?: string;
  url?: string;
  resumed?: boolean;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

/**
 * The API answers validation and endpoint-specific failures with `message` and
 * framework failures with `error`, so both are checked before falling back.
 */
export const kycErrorMessage = (e: any, fallback: string) =>
  e?.response?.data?.message ?? e?.response?.data?.error ?? fallback;

export const isVeriffRecord = (record?: Pick<KycRecord, 'provider'> | null) =>
  String(record?.provider ?? 'VERIFF').toUpperCase() !== 'MANUAL';

export const formatKycName = (name?: UserName | string | null) => {
  if (!name) return '';
  if (typeof name === 'string') return name.trim();
  return `${name.first ?? ''} ${name.last ?? ''}`.trim();
};

export const formatKycUserName = (user?: KycUserSummary | null) =>
  formatKycName(user?.name) || user?.email || '—';

export const formatProviderLabel = (provider?: string) => {
  if (!provider) return '—';
  return String(provider).toUpperCase() === 'MANUAL' ? 'Manual' : 'Veriff';
};

/** Sentence-cases Veriff's snake_case session states and risk labels. */
export const formatVeriffTerm = (value?: string | null) => {
  if (!value) return '—';
  const text = String(value).replace(/_/g, ' ').trim();
  if (!text) return '—';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const formatIdentificationType = (type?: string | null) => formatVeriffTerm(type);

const DECISION_CODES: Record<number, string> = {
  9001: 'Approved',
  9102: 'Declined',
  9103: 'Resubmission requested',
  9104: 'Expired or abandoned',
  9121: 'Review',
};

export const formatDecisionCode = (code?: number | null) => {
  if (code == null) return '—';
  const label = DECISION_CODES[code];
  return label ? `${code} · ${label}` : String(code);
};

/** Veriff scores 0–1; two decimals keeps neighbouring rows comparable. */
export const formatRiskScore = (score?: number | null) => {
  if (score == null || !Number.isFinite(score)) return '—';
  return score.toFixed(2);
};

export const formatNfcValidated = (value?: boolean | null) => {
  if (value == null) return '—';
  return value ? 'Yes' : 'No';
};

/** `user` is a summary on list rows and a bare ObjectId on detail responses. */
export const getKycUserId = (record?: KycRecord | KycListItem | null) => {
  if (!record) return '';
  const anyRecord = record as KycListItem;
  if (anyRecord.user_id) return String(anyRecord.user_id);
  if (typeof record.user === 'string') return record.user;
  return String(record.user?.id ?? record.user?._id ?? '');
};

/**
 * The API serialises records with Mongo's `_id` on some payloads and `id` on
 * others. Normalising here keeps every screen addressing records by `id`.
 */
const normalizeRecord = <T extends KycRecord>(record: T): T => ({
  ...record,
  id: String(record.id ?? record._id ?? ''),
});

export async function fetchKycRecords(params: {
  page?: number;
  limit?: number;
  status?: KycStatus | string;
  provider?: KycProvider | string;
  requires_manual_review?: boolean;
  /** Independent filters, ANDed by the API: one query param per field. */
  name?: string;
  email?: string;
  country_code?: string;
  identification_type?: string;
  id?: string;
  /** Owner's user id — a user's own records. */
  user?: string;
  /** Legacy single pair; still accepted and combined with the above. */
  field?: KycSearchField | string;
  value?: string;
}) {
  const response = await request.get(`${AUTH_URL}/v1/admin/kyc${buildQuery(params)}`);
  return {
    kyc: ((response.data.kyc ?? []) as KycListItem[]).map(normalizeRecord),
    meta: (response.data.meta ?? {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      page_count: 1,
      total_count: (response.data.kyc ?? []).length,
    }) as ListMeta,
  };
}

export async function fetchKycRecord(kycId: string) {
  const response = await request.get(`${AUTH_URL}/v1/admin/kyc/${kycId}`);
  return normalizeRecord((response.data.kyc ?? response.data) as KycRecord);
}

/**
 * Approve (`VERIFIED`) or reject (`CANCELED`). A rejection is the only thing
 * that reopens a record for another attempt, and its `response_message` reaches
 * the customer verbatim.
 */
export async function updateKycRecord(
  kycId: string,
  payload: { status: 'VERIFIED' | 'CANCELED'; response_message?: string },
) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/kyc/${kycId}`, payload);
  return normalizeRecord((response.data.kyc ?? response.data) as KycRecord);
}

export type KycMediaItem = {
  id: string;
  kind: 'image' | 'video';
  name?: string;
  /** Veriff's slot: document-front, document-back, face, document-and-face… */
  context?: string;
  mimetype?: string;
  size?: number;
  timestamp?: string;
};

/**
 * The media Veriff holds for this record's session — proxied live by the API,
 * nothing stored on our side. One call per opened record, not per list row.
 */
export async function fetchKycMedia(kycId: string) {
  const response = await request.get(`${AUTH_URL}/v1/admin/kyc/${kycId}/media`);
  return (response.data.media ?? []) as KycMediaItem[];
}

/**
 * One media file as an object URL. The file endpoint needs the bearer header
 * like every other call, so a plain <img src> cannot load it. Callers revoke
 * the URL when they are done with it.
 */
export async function fetchKycMediaBlob(kycId: string, mediaId: string) {
  const response = await request.get(`${AUTH_URL}/v1/admin/kyc/${kycId}/media/${mediaId}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data as Blob);
}

/** Opens a fresh Veriff session. Every session is billed, so confirm first. */
export async function reverifyKyc(kycId: string, reason?: string) {
  const response = await request.post(
    `${AUTH_URL}/v1/admin/kyc/${kycId}/reverify`,
    reason ? { reason } : {},
  );
  return (response.data.session ?? {}) as ReverifySession;
}

/**
 * A user's own records: the list filters on the owner id directly. (The old
 * email lookup matched nothing — a KYC record carries no email.)
 */
export async function fetchKycRecordsForUser(userId: string) {
  const { kyc } = await fetchKycRecords({ page: 1, limit: 10, user: userId });
  return kyc;
}
